import { create, type StateCreator } from "zustand";
import { subscribeWithSelector, devtools } from "zustand/middleware";
import {
  createConnectionSlice,
  type ConnectionSlice,
} from "./connection.store";
import { createAuthSlice, type AuthSlice } from "./auth.store";
import {
  createTournamentSlice,
  type TournamentSlice,
} from "./tournament.store";
import {
  createTournamentProgressSlice,
  type TournamentProgressSlice,
} from "./tournament-progress.store";
import { createCampaignsSlice, type CampaignsSlice } from "./campaigns.store";
import {
  createNotificationsSlice,
  type NotificationsSlice,
} from "./notifications.store";
import { createPrizeDropSlice, type PrizeDropSlice } from "./prizedrop.store";
import { createMissionsSlice, type MissionsSlice } from "./missions.store";
import {
  createSprintMissionSlice,
  type SprintMissionSlice,
} from "./sprint-mission.store";
import { createJackpotSlice, type JackpotSlice } from "./jackpot.store";
import { createChaseSlice, type ChaseSlice } from "./chase.store";
import { createRaffleSlice, type RaffleSlice } from "./raffle.store";
import { setupStoreSubscriptions } from "./store-subscriptions";
import { createConfigSlice, type ConfigSlice } from "./config.store";
import { createThemeSlice, type ThemeSlice } from "./theme.store";

export type AppStore = ConnectionSlice &
  AuthSlice &
  TournamentSlice &
  TournamentProgressSlice &
  CampaignsSlice &
  NotificationsSlice &
  PrizeDropSlice &
  MissionsSlice &
  SprintMissionSlice &
  JackpotSlice &
  ChaseSlice &
  RaffleSlice &
  ConfigSlice &
  ThemeSlice;

const createStore: StateCreator<AppStore> = (set, get, api) => ({
  ...createConnectionSlice(set as never, get as never, api as never),
  ...createAuthSlice(set as never, get as never, api as never),
  ...createTournamentSlice(set as never, get as never, api as never),
  ...createTournamentProgressSlice(set as never, get as never, api as never),
  ...createCampaignsSlice(set as never, get as never, api as never),
  ...createNotificationsSlice(set as never, get as never, api as never),
  ...createPrizeDropSlice(set as never, get as never, api as never),
  ...createMissionsSlice(set as never, get as never, api as never),
  ...createSprintMissionSlice(set as never, get as never, api as never),
  ...createJackpotSlice(set as never, get as never, api as never),
  ...createChaseSlice(set as never, get as never, api as never),
  ...createRaffleSlice(set as never, get as never, api as never),
  ...createConfigSlice(set as never, get as never, api as never),
  ...createThemeSlice(set as never, get as never, api as never),
});

export const useAppStore = create<AppStore>()(
  devtools(subscribeWithSelector(createStore), {
    name: "AppStore",
    enabled: import.meta.env.DEV,
  })
);

// Setup store subscriptions
setupStoreSubscriptions();

if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as unknown as { store: typeof useAppStore }).store = useAppStore;
}
